import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  TrendingUp,
  Wallet,
  BarChart3,
  Bell,
  Settings,
  LogOut,
  Home,
  Briefcase,
  DollarSign,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Info,
  Menu
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexInvestments } from "../../../services/convexService";
import NotificationBadge from "../../../features/notifications/components/NotificationBadge";
import { Id } from "../../../../backend/_generated/dataModel";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";
import { useLanguage } from "../../../contexts/LanguageContext";

const InvestorDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const { user, logout } = useAuth();
  const { getProjectsForInvestors } = useConvexProjects();
  const { getInvestmentsByInvestor } = useConvexInvestments();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Load investor's investments and available opportunities
  const investments = user ? getInvestmentsByInvestor(user.id) || [] : [];
  const opportunities = getProjectsForInvestors() || [];

  const handleLogout = () => {
    logout();
    navigate("/home");
  };

  // Calculate stats based on real data
  const totalInvested = investments.reduce((acc, inv) => acc + (inv?.amount || 0), 0);
  const activeInvestments = investments.filter((inv) => inv?.status === "confirmed").length;
  const pendingInvestments = investments.filter((inv) =>
    inv?.status === "pending" ||
    inv?.status === "project_holder_approved" ||
    inv?.status === "admin_approved"
  ).length;
  const totalExpectedReturn = investments.reduce((acc, inv) => acc + (inv?.amount * 0.15 || 0), 0); // Using 15% as example ROI

  // Helper function to get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "confirmed":
        return "default";
      case "pending":
      case "project_holder_approved":
      case "admin_approved":
        return "secondary";
      case "rejected":
        return "destructive";
      case "refunded":
        return "outline";
      default:
        return "outline";
    }
  };

  // Helper function to get status display text
  const getStatusDisplayText = (status: string) => {
    return t(`dashboard.investor.status.${status}`);
  };

  return (
    <div className="min-h-screen bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => document.getElementById('mobile-sidebar')?.classList.toggle('hidden')}
        >
          <Menu className="w-4 h-4" />
        </Button>
      </div>

      {/* Mobile Sidebar */}
      <aside
        id="mobile-sidebar"
        className="lg:hidden fixed inset-0 z-40 bg-black/50 hidden"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.target as HTMLElement).classList.add('hidden');
          }
        }}
      >
        <div className="w-64 h-full bg-card p-4 ml-auto">
          {/* Logo and Text removed from sidebar header */}
          <div className="h-8"></div>

          <nav className="space-y-2 mb-8">
            <Button
              variant={activeTab === "overview" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("overview");
                document.getElementById('mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Home className="w-4 h-4" />
              {t("dashboard.investor.overview")}
            </Button>
            <Button
              variant={activeTab === "investments" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("investments");
                document.getElementById('mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Briefcase className="w-4 h-4" />
              {t("dashboard.investor.investments")}
            </Button>
            <Button
              variant={activeTab === "opportunities" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("opportunities");
                document.getElementById('mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <DollarSign className="w-4 h-4" />
              {t("dashboard.investor.opportunities")}
            </Button>
            <Button
              variant={activeTab === "notifications" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("notifications");
                document.getElementById('mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Bell className="w-4 h-4" />
              {t("dashboard.investor.notifications")}
              {user && (
                <NotificationBadge userId={user.id as Id<"users">} />
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3"
              onClick={() => {
                navigate("/settings/investor");
                document.getElementById('mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Settings className="w-4 h-4" />
              {t("settings.title")}
            </Button>
          </nav>

          <div className="space-y-2">
            <Link to="/home" onClick={() => document.getElementById('mobile-sidebar')?.classList.add('hidden')}>
              <Button variant="outline" className="w-full justify-start gap-3">
                <Home className="w-4 h-4" />
                {t("navigation.home")}
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="fixed right-0 top-0 h-full w-64 bg-card border-l border-border p-4 hidden lg:block" dir={language === "ar" ? "rtl" : "ltr"}>
        {/* Logo and Text removed from sidebar header */}
        <div className="h-8"></div>

        <nav className="space-y-2">
          <Button
            variant={activeTab === "overview" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("overview")}
          >
            <Home className="w-4 h-4" />
            {t("dashboard.investor.overview")}
          </Button>
          <Button
            variant={activeTab === "investments" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("investments")}
          >
            <Briefcase className="w-4 h-4" />
            {t("dashboard.investor.investments")}
          </Button>
          <Button
            variant={activeTab === "opportunities" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("opportunities")}
          >
            <DollarSign className="w-4 h-4" />
            {t("dashboard.investor.opportunities")}
          </Button>
          <Button
            variant={activeTab === "notifications" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("notifications")}
          >
            <Bell className="w-4 h-4" />
            {t("dashboard.investor.notifications")}
            {user && (
              <NotificationBadge userId={user.id as Id<"users">} />
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={() => navigate("/settings/investor")}
          >
            <Settings className="w-4 h-4" />
            {t("settings.title")}
          </Button>
        </nav>

        <div className="absolute bottom-4 right-4 left-4 space-y-2">
          <Link to="/home">
            <Button variant="outline" className="w-full justify-start gap-3">
              <Home className="w-4 h-4" />
              {t("navigation.home")}
            </Button>
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            {t("common.logout")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:mr-64 p-6" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="flex items-center justify-between mb-8 w-full">
          {/* Text content */}
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("common.welcome")}, {user?.name || t("dashboard.investor.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("dashboard.investor.subtitle") || t("common.dashboard")}
            </p>
          </div>

          {/* Logo on far right */}
          <img
            src="/logo.png"
            alt="Knowvest.plus"
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Account Status Warning */}
        {user?.status !== "approved" && (
          <Card className="mb-6 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {user?.status === "pending_approval"
                      ? t("status.pending_approval")
                      : user?.status === "rejected"
                        ? t("status.rejected")
                        : user?.status === "suspended" ? t("status.suspended") : t("status.approved")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.status === "pending_approval"
                      ? t("waiting.investor.message")
                      : user?.status === "rejected"
                        ? t("waiting.investor.investmentCapacity") // Using placeholder for now
                        : t("waiting.investor.investmentPreferences")} // Using placeholder for now
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.investor.totalInvestments")}</p>
                  <CurrencyDisplay amount={totalInvested} className="text-2xl font-bold text-foreground" />
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.investor.activeInvestments")}</p>
                  <p className="text-2xl font-bold text-foreground">{activeInvestments}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.investor.pendingInvestments")}</p>
                  <p className="text-2xl font-bold text-foreground">{pendingInvestments}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.investor.newOpportunities")}</p>
                  <p className="text-2xl font-bold text-foreground">{opportunities.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="lg:hidden grid grid-cols-3 w-full">
            <TabsTrigger value="overview">{t("dashboard.investor.overview")}</TabsTrigger>
            <TabsTrigger value="investments">{t("dashboard.investor.investments")}</TabsTrigger>
            <TabsTrigger value="opportunities">{t("dashboard.investor.opportunities")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Active Investments */}
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.investor.myInvestments")}</CardTitle>
                <CardDescription>{t("dashboard.investor.investmentHistory")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {investments.length > 0 ? (
                    investments.slice(0, 2).map((investment) => (
                      <div key={investment._id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">{t("dashboard.investor.investmentHistory")}</h4>
                          <p className="text-sm text-muted-foreground">
                            <CurrencyDisplay amount={investment.amount} /> • {t("common.status")}: {getStatusDisplayText(investment.status)}
                          </p>
                          <Progress value={50} className="mt-2 h-2" />
                        </div>
                        <Badge variant={getStatusBadgeVariant(investment.status)}>
                          {getStatusDisplayText(investment.status)}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4">{t("common.noData") || "No active investments yet"}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* New Opportunities */}
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.investor.availableOpportunities")}</CardTitle>
                <CardDescription>{t("dashboard.investor.opportunities")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {opportunities.length > 0 ? (
                    opportunities.slice(0, 2).map((opportunity) => (
                      <Link to={`/projects/${opportunity._id}`} key={opportunity._id} className="block">
                        <div className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-foreground">{opportunity.title}</h4>
                            <Badge variant="outline">{opportunity.category}</Badge>
                          </div>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex justify-between">
                              <span>{t("common.target") || t("common.goal")}:</span>
                              <span><CurrencyDisplay amount={opportunity.fundingGoal} /></span>
                            </div>
                            <Progress value={(opportunity.currentFunding / opportunity.fundingGoal) * 100} className="h-2" />
                            <div className="flex justify-between">
                              <span>{t("common.expectedReturn") || "Expected Return:"}:</span>
                              <span className="text-primary font-medium">{opportunity.expectedROI}%</span>
                            </div>
                          </div>
                          <Button className="w-full mt-4" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            {t("common.view")}
                          </Button>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">{t("common.noData") || "No investment opportunities available at the moment"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="investments">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.investor.investmentHistory")}</CardTitle>
                <CardDescription>{t("dashboard.investor.investmentHistory")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {investments.length > 0 ? (
                    investments.map((investment) => (
                      <div key={investment._id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">{t("dashboard.investor.investmentHistory")}</h4>
                          <p className="text-sm text-muted-foreground">
                            <CurrencyDisplay amount={investment.amount} /> • {t("common.status")}: {getStatusDisplayText(investment.status)}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {t("common.date") || "Date"}: {new Date(investment.createdAt).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <Info className="w-3 h-3" />
                            {t("common.expectedReturn") || "Expected Return"}: <CurrencyDisplay amount={investment.amount * 0.15} />
                          </div>
                          <Progress value={50} className="mt-2 h-2" />
                        </div>
                        <div className="text-left">
                          <Badge variant={getStatusBadgeVariant(investment.status)}>
                            {getStatusDisplayText(investment.status)}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {investment.status === "confirmed" ? "100% Complete" :
                              investment.status === "pending" || investment.status === "project_holder_approved" || investment.status === "admin_approved" ? "Processing" :
                                "Completed"}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4">{t("common.noData") || "No investments yet"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="opportunities">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.investor.availableOpportunities")}</CardTitle>
                <CardDescription>{t("dashboard.investor.opportunities")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {opportunities.length > 0 ? (
                    opportunities.map((opportunity) => (
                      <Link to={`/projects/${opportunity._id}`} key={opportunity._id} className="block">
                        <div className="p-6 border border-border rounded-lg hover:border-primary transition-colors">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-lg text-foreground">{opportunity.title}</h4>
                            <Badge variant="outline">{opportunity.category}</Badge>
                          </div>
                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between text-muted-foreground">
                              <span>{t("common.target")}:</span>
                              <span className="font-medium text-foreground"><CurrencyDisplay amount={opportunity.fundingGoal} /></span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span>{t("common.raised") || "Raised:"}:</span>
                              <span className="font-medium text-foreground"><CurrencyDisplay amount={opportunity.currentFunding} /></span>
                            </div>
                            <Progress value={(opportunity.currentFunding / opportunity.fundingGoal) * 100} className="h-2" />
                            <div className="flex justify-between text-muted-foreground">
                              <span>{t("common.minInvestment")}:</span>
                              <span className="font-medium text-foreground"><CurrencyDisplay amount={opportunity.minInvestment} /></span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span>{t("common.expectedReturn") || "Expected Return:"}:</span>
                              <span className="font-bold text-primary">{opportunity.expectedROI}%</span>
                            </div>

                          </div>
                          <Button className="w-full mt-4">
                            {t("common.view")}
                          </Button>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8 col-span-2">{t("common.noData") || "No investment opportunities available at the moment"}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.investor.notifications")}</CardTitle>
                <CardDescription>{t("common.notifications")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground text-center py-8">
                    {t("common.noData") || "No notifications available"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
};

export default InvestorDashboard;